// API service layer for fetching real data from open sources

import {
  MarketZone,
  WeatherForecast,
  MarketData,
  ModelAccuracy,
  PricePrediction,
} from '../types/data'

const OPEN_METEO_BASE = 'https://api.open-meteo.com/v1'
const ENTSOE_BASE = 'https://web-api.tp.entsoe.eu/api'
const ENTSOE_API_TOKEN = '4aaa42a0-565a-4126-a233-60aa0afe2cae'

// Country coordinates for weather data
const ZONE_COORDINATES: Record<
  string,
  { lat: number; lon: number; name: string }
> = {
  // Europe - Countries
  DE: { lat: 51.1657, lon: 10.4515, name: 'Germany' },
  ES: { lat: 40.4637, lon: -3.7492, name: 'Spain' },
  UK: { lat: 55.3781, lon: -3.436, name: 'United Kingdom' },
  FR: { lat: 46.2276, lon: 2.2137, name: 'France' },
  IT: { lat: 41.8719, lon: 12.5674, name: 'Italy' },
  NL: { lat: 52.1326, lon: 5.2913, name: 'Netherlands' },
  BE: { lat: 50.5039, lon: 4.4699, name: 'Belgium' },
  AT: { lat: 47.5162, lon: 14.5501, name: 'Austria' },
  CH: { lat: 46.8182, lon: 8.2275, name: 'Switzerland' },
  PL: { lat: 51.9194, lon: 19.1451, name: 'Poland' },
  SE: { lat: 60.1282, lon: 18.6435, name: 'Sweden' },
  NO: { lat: 60.472, lon: 8.4689, name: 'Norway' },
  DK: { lat: 56.2639, lon: 9.5018, name: 'Denmark' },
  FI: { lat: 61.9241, lon: 25.7482, name: 'Finland' },
  PT: { lat: 39.3999, lon: -8.2245, name: 'Portugal' },
  GR: { lat: 39.0742, lon: 21.8243, name: 'Greece' },
  CZ: { lat: 49.8175, lon: 15.473, name: 'Czech Republic' },
  IE: { lat: 53.4129, lon: -8.2439, name: 'Ireland' },

  // Europe - Major Cities
  'GB-LON': { lat: 51.5074, lon: -0.1278, name: 'London' },
  'FR-PAR': { lat: 48.8566, lon: 2.3522, name: 'Paris' },
  'DE-BER': { lat: 52.52, lon: 13.405, name: 'Berlin' },
  'DE-MUN': { lat: 48.1351, lon: 11.582, name: 'Munich' },
  'DE-HAM': { lat: 53.5511, lon: 9.9937, name: 'Hamburg' },
  'ES-MAD': { lat: 40.4168, lon: -3.7038, name: 'Madrid' },
  'ES-BCN': { lat: 41.3851, lon: 2.1734, name: 'Barcelona' },
  'IT-ROM': { lat: 41.9028, lon: 12.4964, name: 'Rome' },
  'IT-MIL': { lat: 45.4642, lon: 9.19, name: 'Milan' },
  'NL-AMS': { lat: 52.3676, lon: 4.9041, name: 'Amsterdam' },
  'BE-BRU': { lat: 50.8503, lon: 4.3517, name: 'Brussels' },
  'AT-VIE': { lat: 48.2082, lon: 16.3738, name: 'Vienna' },
  'CH-ZRH': { lat: 47.3769, lon: 8.5417, name: 'Zurich' },
  'SE-STO': { lat: 59.3293, lon: 18.0686, name: 'Stockholm' },
  'NO-OSL': { lat: 59.9139, lon: 10.7522, name: 'Oslo' },
  'DK-CPH': { lat: 55.6761, lon: 12.5683, name: 'Copenhagen' },
  'FI-HEL': { lat: 60.1699, lon: 24.9384, name: 'Helsinki' },
  'PT-LIS': { lat: 38.7223, lon: -9.1393, name: 'Lisbon' },
  'GR-ATH': { lat: 37.9838, lon: 23.7275, name: 'Athens' },
  'CZ-PRG': { lat: 50.0755, lon: 14.4378, name: 'Prague' },
  'IE-DUB': { lat: 53.3498, lon: -6.2603, name: 'Dublin' },
  'PL-WAR': { lat: 52.2297, lon: 21.0122, name: 'Warsaw' },

  // North America - Countries
  US: { lat: 37.0902, lon: -95.7129, name: 'United States' },
  CA: { lat: 56.1304, lon: -106.3468, name: 'Canada' },
  MX: { lat: 23.6345, lon: -102.5528, name: 'Mexico' },

  // North America - Major Cities
  'US-NYC': { lat: 40.7128, lon: -74.006, name: 'New York' },
  'US-LAX': { lat: 34.0522, lon: -118.2437, name: 'Los Angeles' },
  'US-CHI': { lat: 41.8781, lon: -87.6298, name: 'Chicago' },
  'US-SFO': { lat: 37.7749, lon: -122.4194, name: 'San Francisco' },
  'US-SEA': { lat: 47.6062, lon: -122.3321, name: 'Seattle' },
  'US-BOS': { lat: 42.3601, lon: -71.0589, name: 'Boston' },
  'US-MIA': { lat: 25.7617, lon: -80.1918, name: 'Miami' },
  'US-HOU': { lat: 29.7604, lon: -95.3698, name: 'Houston' },
  'US-DAL': { lat: 32.7767, lon: -96.797, name: 'Dallas' },
  'US-ATL': { lat: 33.749, lon: -84.388, name: 'Atlanta' },
  'US-WAS': { lat: 38.9072, lon: -77.0369, name: 'Washington DC' },
  'CA-TOR': { lat: 43.6532, lon: -79.3832, name: 'Toronto' },
  'CA-VAN': { lat: 49.2827, lon: -123.1207, name: 'Vancouver' },
  'CA-MTL': { lat: 45.5017, lon: -73.5673, name: 'Montreal' },
  'MX-MEX': { lat: 19.4326, lon: -99.1332, name: 'Mexico City' },

  // Asia - Countries
  JP: { lat: 36.2048, lon: 138.2529, name: 'Japan' },
  CN: { lat: 35.8617, lon: 104.1954, name: 'China' },
  KR: { lat: 35.9078, lon: 127.7669, name: 'South Korea' },
  IN: { lat: 20.5937, lon: 78.9629, name: 'India' },
  SG: { lat: 1.3521, lon: 103.8198, name: 'Singapore' },
  TH: { lat: 15.87, lon: 100.9925, name: 'Thailand' },
  MY: { lat: 4.2105, lon: 101.9758, name: 'Malaysia' },
  ID: { lat: -0.7893, lon: 113.9213, name: 'Indonesia' },
  VN: { lat: 14.0583, lon: 108.2772, name: 'Vietnam' },
  PH: { lat: 12.8797, lon: 121.774, name: 'Philippines' },

  // Asia - Major Cities
  'JP-TYO': { lat: 35.6762, lon: 139.6503, name: 'Tokyo' },
  'JP-OSA': { lat: 34.6937, lon: 135.5023, name: 'Osaka' },
  'CN-BEI': { lat: 39.9042, lon: 116.4074, name: 'Beijing' },
  'CN-SHA': { lat: 31.2304, lon: 121.4737, name: 'Shanghai' },
  HK: { lat: 22.3193, lon: 114.1694, name: 'Hong Kong' },
  'KR-SEL': { lat: 37.5665, lon: 126.978, name: 'Seoul' },
  'IN-BOM': { lat: 19.076, lon: 72.8777, name: 'Mumbai' },
  'IN-DEL': { lat: 28.7041, lon: 77.1025, name: 'Delhi' },
  'IN-BLR': { lat: 12.9716, lon: 77.5946, name: 'Bangalore' },
  'SG-SIN': { lat: 1.3521, lon: 103.8198, name: 'Singapore' },
  'TH-BKK': { lat: 13.7563, lon: 100.5018, name: 'Bangkok' },
  'MY-KUL': { lat: 3.139, lon: 101.6869, name: 'Kuala Lumpur' },
  'ID-JKT': { lat: -6.2088, lon: 106.8456, name: 'Jakarta' },
  'PH-MNL': { lat: 14.5995, lon: 120.9842, name: 'Manila' },
  'VN-SGN': { lat: 10.8231, lon: 106.6297, name: 'Ho Chi Minh City' },

  // Middle East
  'AE-DXB': { lat: 25.2048, lon: 55.2708, name: 'Dubai' },
  'AE-AUH': { lat: 24.4539, lon: 54.3773, name: 'Abu Dhabi' },
  'IL-TLV': { lat: 32.0853, lon: 34.7818, name: 'Tel Aviv' },
  'TR-IST': { lat: 41.0082, lon: 28.9784, name: 'Istanbul' },
  'SA-RUH': { lat: 24.7136, lon: 46.6753, name: 'Riyadh' },

  // Oceania
  AU: { lat: -25.2744, lon: 133.7751, name: 'Australia' },
  NZ: { lat: -40.9006, lon: 174.886, name: 'New Zealand' },
  'AU-SYD': { lat: -33.8688, lon: 151.2093, name: 'Sydney' },
  'AU-MEL': { lat: -37.8136, lon: 144.9631, name: 'Melbourne' },
  'AU-BNE': { lat: -27.4698, lon: 153.0251, name: 'Brisbane' },
  'AU-PER': { lat: -31.9505, lon: 115.8605, name: 'Perth' },
  'NZ-AKL': { lat: -36.8485, lon: 174.7633, name: 'Auckland' },

  // South America
  BR: { lat: -14.235, lon: -51.9253, name: 'Brazil' },
  AR: { lat: -38.4161, lon: -63.6167, name: 'Argentina' },
  CL: { lat: -35.6751, lon: -71.543, name: 'Chile' },
  'BR-SAO': { lat: -23.5505, lon: -46.6333, name: 'São Paulo' },
  'BR-RIO': { lat: -22.9068, lon: -43.1729, name: 'Rio de Janeiro' },
  'AR-BUE': { lat: -34.6037, lon: -58.3816, name: 'Buenos Aires' },
  'CL-SCL': { lat: -33.4489, lon: -70.6693, name: 'Santiago' },

  // Africa
  ZA: { lat: -30.5595, lon: 22.9375, name: 'South Africa' },
  'ZA-CPT': { lat: -33.9249, lon: 18.4241, name: 'Cape Town' },
  'ZA-JNB': { lat: -26.2041, lon: 28.0473, name: 'Johannesburg' },
  'EG-CAI': { lat: 30.0444, lon: 31.2357, name: 'Cairo' },
  'NG-LOS': { lat: 6.5244, lon: 3.3792, name: 'Lagos' },
}

/**
 * Fetch weather forecast data from Open-Meteo API
 * Provides ECMWF (EC-IFS) and GFS model data
 */
export async function fetchWeatherForecast(
  zone: MarketZone,
): Promise<WeatherForecast[]> {
  // Check if coordinates exist for this zone
  const coordinates = ZONE_COORDINATES[zone]
  if (!coordinates) {
    console.warn(`No coordinates found for zone: ${zone}`)
    return []
  }

  const { lat, lon } = coordinates

  const params = new URLSearchParams({
    latitude: lat.toString(),
    longitude: lon.toString(),
    hourly:
      'temperature_2m,windspeed_10m,winddirection_10m,cloudcover,precipitation,surface_pressure,shortwave_radiation',
    forecast_days: '7',
    timezone: 'auto',
  })

  try {
    const response = await fetch(`${OPEN_METEO_BASE}/forecast?${params}`)

    if (!response.ok) {
      throw new Error(
        `API request failed: ${response.status} ${response.statusText}`,
      )
    }

    const data = await response.json()

    if (!data.hourly || !data.hourly.time) {
      throw new Error('Invalid API response format')
    }

    // Transform Open-Meteo data to our format
    const forecasts: WeatherForecast[] = []

    for (let i = 0; i < data.hourly.time.length; i++) {
      // Create forecast for EC-IFS (primary model from Open-Meteo)
      forecasts.push({
        timestamp: new Date(data.hourly.time[i]),
        location: { lat, lon, zone },
        model: 'EC-IFS',
        parameters: {
          windSpeed: data.hourly.windspeed_10m[i] || 0,
          windDirection: data.hourly.winddirection_10m[i] || 0,
          temperature: data.hourly.temperature_2m[i] || 0,
          solarRadiation: data.hourly.shortwave_radiation[i] || 0,
          precipitation: data.hourly.precipitation[i] || 0,
          cloudCover: data.hourly.cloudcover[i] || 0,
          pressure: data.hourly.surface_pressure[i] || 1013,
        },
        forecastHorizon: i,
      })

      // Simulate GFS model with slight variations
      forecasts.push({
        timestamp: new Date(data.hourly.time[i]),
        location: { lat, lon, zone },
        model: 'GFS',
        parameters: {
          windSpeed:
            (data.hourly.windspeed_10m[i] || 0) * (0.95 + Math.random() * 0.1),
          windDirection: data.hourly.winddirection_10m[i] || 0,
          temperature:
            (data.hourly.temperature_2m[i] || 0) + (Math.random() - 0.5) * 2,
          solarRadiation:
            (data.hourly.shortwave_radiation[i] || 0) *
            (0.9 + Math.random() * 0.2),
          precipitation: data.hourly.precipitation[i] || 0,
          cloudCover: data.hourly.cloudcover[i] || 0,
          pressure: data.hourly.surface_pressure[i] || 1013,
        },
        forecastHorizon: i,
      })

      // Simulate EPT-2 (Jua's proprietary model) with better accuracy
      forecasts.push({
        timestamp: new Date(data.hourly.time[i]),
        location: { lat, lon, zone },
        model: 'EPT-2',
        parameters: {
          windSpeed:
            (data.hourly.windspeed_10m[i] || 0) * (0.98 + Math.random() * 0.04),
          windDirection: data.hourly.winddirection_10m[i] || 0,
          temperature:
            (data.hourly.temperature_2m[i] || 0) + (Math.random() - 0.5) * 1,
          solarRadiation:
            (data.hourly.shortwave_radiation[i] || 0) *
            (0.95 + Math.random() * 0.1),
          precipitation: data.hourly.precipitation[i] || 0,
          cloudCover: data.hourly.cloudcover[i] || 0,
          pressure: data.hourly.surface_pressure[i] || 1013,
        },
        forecastHorizon: i,
      })
    }

    return forecasts
  } catch (error) {
    console.error(`Error fetching weather forecast for ${zone}:`, error)
    return []
  }
}

/**
 * Generate realistic market data based on actual ENTSO-E patterns
 * Uses real-world data observations from European electricity markets
 */
export function generateMarketData(
  zone: MarketZone,
  hours: number = 24,
): MarketData[] {
  const data: MarketData[] = []
  const now = new Date()

  // Real-world base values and patterns from ENTSO-E observations (2023-2024)
  const marketProfiles: Record<
    MarketZone,
    {
      priceRange: { min: number; max: number; avg: number }
      loadRange: { min: number; max: number }
      generationMix: {
        nuclear: number
        coal: number
        gas: number
        wind: number
        solar: number
        hydro: number
        biomass: number
        other: number
      }
      peakHours: { start: number; end: number }
      characteristics: string
    }
  > = {
    // Germany: Renewable leader, coal phase-out, high industrial load
    DE: {
      priceRange: { min: 45, max: 135, avg: 85 },
      loadRange: { min: 42000, max: 68000 },
      generationMix: {
        nuclear: 0, // Phased out
        coal: 18, // Declining
        gas: 12,
        wind: 28, // High wind capacity
        solar: 12, // High solar capacity
        hydro: 4,
        biomass: 8,
        other: 18,
      },
      peakHours: { start: 8, end: 20 },
      characteristics: 'High renewables, industrial load',
    },

    // Spain: Solar powerhouse, growing wind, lower industrial load
    ES: {
      priceRange: { min: 35, max: 110, avg: 70 },
      loadRange: { min: 28000, max: 42000 },
      generationMix: {
        nuclear: 20,
        coal: 5, // Minimal
        gas: 15,
        wind: 22,
        solar: 18, // Very high solar
        hydro: 12,
        biomass: 4,
        other: 4,
      },
      peakHours: { start: 9, end: 21 },
      characteristics: 'Solar leader, Mediterranean climate',
    },

    // France: Nuclear dominant, stable prices, high exports
    FR: {
      priceRange: { min: 30, max: 95, avg: 65 },
      loadRange: { min: 48000, max: 72000 },
      generationMix: {
        nuclear: 65, // Dominant
        coal: 1, // Minimal
        gas: 8,
        wind: 10,
        solar: 5,
        hydro: 8,
        biomass: 2,
        other: 1,
      },
      peakHours: { start: 7, end: 19 },
      characteristics: 'Nuclear baseload, stable supply',
    },

    // UK: Gas + offshore wind, higher prices, island grid
    UK: {
      priceRange: { min: 65, max: 165, avg: 105 },
      loadRange: { min: 32000, max: 48000 },
      generationMix: {
        nuclear: 15,
        coal: 2, // Phased out
        gas: 38, // High gas dependency
        wind: 25, // Growing offshore wind
        solar: 5,
        hydro: 2,
        biomass: 8,
        other: 5,
      },
      peakHours: { start: 7, end: 19 },
      characteristics: 'Gas + offshore wind, higher prices',
    },

    // Italy: Gas heavy, high imports, solar growth
    IT: {
      priceRange: { min: 55, max: 145, avg: 95 },
      loadRange: { min: 35000, max: 52000 },
      generationMix: {
        nuclear: 0,
        coal: 5,
        gas: 45, // Very high
        wind: 8,
        solar: 12,
        hydro: 18,
        biomass: 6,
        other: 6,
      },
      peakHours: { start: 8, end: 20 },
      characteristics: 'Gas dependent, high imports',
    },

    // Netherlands: Gas hub, growing offshore wind
    NL: {
      priceRange: { min: 50, max: 125, avg: 88 },
      loadRange: { min: 12000, max: 18000 },
      generationMix: {
        nuclear: 3,
        coal: 12,
        gas: 48,
        wind: 18,
        solar: 8,
        hydro: 0,
        biomass: 8,
        other: 3,
      },
      peakHours: { start: 8, end: 19 },
      characteristics: 'Gas hub, offshore wind growth',
    },

    // Belgium: Nuclear + imports, small grid
    BE: {
      priceRange: { min: 48, max: 118, avg: 82 },
      loadRange: { min: 9000, max: 14000 },
      generationMix: {
        nuclear: 45,
        coal: 2,
        gas: 25,
        wind: 12,
        solar: 6,
        hydro: 1,
        biomass: 6,
        other: 3,
      },
      peakHours: { start: 7, end: 19 },
      characteristics: 'Nuclear + imports',
    },

    // Austria: Hydro dominant, Alpine storage
    AT: {
      priceRange: { min: 40, max: 105, avg: 75 },
      loadRange: { min: 6500, max: 10000 },
      generationMix: {
        nuclear: 0,
        coal: 5,
        gas: 18,
        wind: 8,
        solar: 4,
        hydro: 55, // Very high
        biomass: 8,
        other: 2,
      },
      peakHours: { start: 7, end: 19 },
      characteristics: 'Hydro storage, Alpine resources',
    },
  }

  const profile = marketProfiles[zone] || marketProfiles.DE

  // Seeded random for deterministic but varied data
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed) * 10000
    return x - Math.floor(x)
  }

  // Get current season for seasonal adjustments
  const month = now.getMonth()
  const isSummer = month >= 5 && month <= 8
  const isWinter = month >= 11 || month <= 2

  for (let i = 0; i < hours; i++) {
    const timestamp = new Date(now.getTime() - (hours - i) * 60 * 60 * 1000)
    const hour = timestamp.getHours()
    const dayOfWeek = timestamp.getDay()
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

    // Seed for deterministic randomness
    const seed = Math.floor(timestamp.getTime() / (60 * 60 * 1000))

    // Time-of-day patterns
    const isPeakHour =
      hour >= profile.peakHours.start && hour <= profile.peakHours.end
    const peakMultiplier = isPeakHour ? 1.25 : 0.75
    const weekendMultiplier = isWeekend ? 0.85 : 1.0

    // Seasonal adjustments
    const seasonalLoadMultiplier = isWinter ? 1.15 : isSummer ? 1.05 : 1.0
    const seasonalPriceMultiplier = isWinter ? 1.2 : isSummer ? 1.1 : 1.0

    // Price calculation with realistic volatility
    const basePrice = profile.priceRange.avg
    const priceVolatility = seededRandom(seed + 1) * 0.3 - 0.15 // ±15%
    const spotPrice =
      basePrice *
      peakMultiplier *
      seasonalPriceMultiplier *
      (1 + priceVolatility) *
      weekendMultiplier

    // Clamp to realistic range
    const clampedPrice = Math.max(
      profile.priceRange.min,
      Math.min(profile.priceRange.max, spotPrice),
    )

    // Load calculation
    const baseLoad = (profile.loadRange.min + profile.loadRange.max) / 2
    const loadVariation = seededRandom(seed + 2) * 0.15 - 0.075 // ±7.5%
    const totalLoad =
      baseLoad *
      peakMultiplier *
      seasonalLoadMultiplier *
      (1 + loadVariation) *
      weekendMultiplier

    // Solar generation (follows sun curve)
    const solarHour = Math.max(0, Math.min(14, hour - 6)) // 6am to 8pm
    const solarMultiplier = Math.max(0, Math.sin((solarHour * Math.PI) / 14))
    const solarCapacity = (totalLoad * profile.generationMix.solar) / 100
    const solarGeneration =
      solarCapacity * solarMultiplier * (isSummer ? 1.3 : 0.7)

    // Wind generation (more variable, higher at night)
    const windVariability = seededRandom(seed + 3)
    const nightWindBoost = !isPeakHour ? 1.2 : 1.0
    const windCapacity = (totalLoad * profile.generationMix.wind) / 100
    const windGeneration =
      windCapacity *
      (0.3 + windVariability * 0.7) *
      nightWindBoost *
      (isWinter ? 1.2 : 0.9)

    // Other generation sources (more stable)
    const nuclearGeneration = (totalLoad * profile.generationMix.nuclear) / 100
    const coalGeneration =
      ((totalLoad * profile.generationMix.coal) / 100) *
      (1 + seededRandom(seed + 4) * 0.2 - 0.1)
    const gasGeneration =
      ((totalLoad * profile.generationMix.gas) / 100) *
      peakMultiplier * // Gas follows demand
      (1 + seededRandom(seed + 5) * 0.3 - 0.15)
    const hydroGeneration =
      ((totalLoad * profile.generationMix.hydro) / 100) *
      (1 + seededRandom(seed + 6) * 0.2 - 0.1)
    const biomassGeneration = (totalLoad * profile.generationMix.biomass) / 100
    const otherGeneration = (totalLoad * profile.generationMix.other) / 100

    // Cross-border flow (realistic patterns)
    const crossBorderFlow =
      zone === 'FR'
        ? -3000 - seededRandom(seed + 7) * 2000 // France exports
        : zone === 'IT'
          ? 2000 + seededRandom(seed + 7) * 2000 // Italy imports
          : (seededRandom(seed + 7) - 0.5) * 3000

    data.push({
      timestamp,
      zone,
      spotPrice: clampedPrice,
      dayAheadPrice: clampedPrice * (0.98 + seededRandom(seed + 8) * 0.04),
      generation: {
        wind: Math.max(0, windGeneration),
        solar: Math.max(0, solarGeneration),
        nuclear: Math.max(0, nuclearGeneration),
        gas: Math.max(0, gasGeneration),
        coal: Math.max(0, coalGeneration),
        hydro: Math.max(0, hydroGeneration),
        biomass: Math.max(0, biomassGeneration),
        other: Math.max(0, otherGeneration),
      },
      load: totalLoad,
      capacity: {
        installed: totalLoad * 1.5,
        available: totalLoad * 1.3,
      },
      crossBorderFlow,
    })
  }

  return data
}

/**
 * Fetch real electricity prices from ENTSO-E API
 * Uses a CORS proxy to bypass browser restrictions
 *
 * NOTE: ENTSO-E API has strict CORS policies. In production, use a backend proxy.
 * Current implementation uses public CORS proxy which may be unreliable.
 */

// Cache to prevent redundant API calls
const entsoeCache = new Map<string, { data: MarketData[]; timestamp: number }>()
const CACHE_DURATION = 10 * 60 * 1000 // 10 minutes (increased from 5)

// Track failed attempts to prevent retry storms
const failedAttempts = new Map<string, { count: number; lastAttempt: number }>()
const MAX_RETRIES = 2 // Only try twice
const RETRY_COOLDOWN = 60 * 1000 // 1 minute cooldown after failures

export async function fetchEntsoeMarketData(
  zone: MarketZone,
  startDate: Date,
  endDate: Date,
): Promise<MarketData[]> {
  console.log(`🔍 Attempting to fetch ENTSO-E data for ${zone}`)

  // Check cache first
  const cacheKey = `${zone}-${startDate.toISOString()}-${endDate.toISOString()}`
  const cached = entsoeCache.get(cacheKey)

  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`📦 Using cached data for ${zone}`)
    return cached.data
  }

  // Check if we've failed too many times recently
  const failureRecord = failedAttempts.get(cacheKey)
  if (failureRecord) {
    const timeSinceLastAttempt = Date.now() - failureRecord.lastAttempt
    if (
      failureRecord.count >= MAX_RETRIES &&
      timeSinceLastAttempt < RETRY_COOLDOWN
    ) {
      console.log(
        `⏸️  ${zone}: Too many failures, using fallback (${failureRecord.count} attempts)`,
      )
      const fallbackData = generateMarketData(zone, 24)
      return fallbackData
    }
  }

  // ENTSO-E domain codes for countries
  const domainCodes: Record<string, string> = {
    DE: '10Y1001A1001A83F', // Germany
    ES: '10YES-REE------0', // Spain
    FR: '10YFR-RTE------C', // France
    IT: '10YIT-GRTN-----B', // Italy
    NL: '10YNL----------L', // Netherlands
    BE: '10YBE----------2', // Belgium
    AT: '10YAT-APG------L', // Austria
    UK: '10YGB----------A', // United Kingdom
  }

  const domainCode = domainCodes[zone]
  if (!domainCode) {
    console.warn(`❌ No ENTSO-E domain code for zone: ${zone}`)
    return generateMarketData(zone, 24)
  }

  console.log(`🌐 Domain code for ${zone}: ${domainCode}`)

  try {
    // Build ENTSO-E API URL
    const entsoeParams = new URLSearchParams({
      securityToken: ENTSOE_API_TOKEN,
      documentType: 'A44', // Day-ahead prices
      in_Domain: domainCode,
      out_Domain: domainCode,
      periodStart: formatEntsoeDate(startDate),
      periodEnd: formatEntsoeDate(endDate),
    })

    const entsoeUrl = `${ENTSOE_BASE}?${entsoeParams}`
    console.log(`📡 ENTSO-E URL for ${zone}:`, entsoeUrl)

    // Use CORS proxy with timeout
    const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(entsoeUrl)}`
    console.log(`🔄 Using proxy for ${zone}`)

    // Add timeout to prevent hanging requests
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000) // Increased to 10s for debugging

    const response = await fetch(proxyUrl, {
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    console.log(`📥 Response status for ${zone}: ${response.status}`)

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const xmlText = await response.text()
    console.log(`📄 XML received for ${zone}, length: ${xmlText.length} chars`)
    console.log(`📄 XML preview for ${zone}:`, xmlText.substring(0, 500))

    // Parse XML to extract price data
    const priceData = parseEntsoeXML(xmlText, zone)

    if (priceData.length > 0) {
      // Success! Clear failure record and cache result
      failedAttempts.delete(cacheKey)
      entsoeCache.set(cacheKey, { data: priceData, timestamp: Date.now() })

      console.log(
        `✅ Successfully loaded ${priceData.length} data points for ${zone}`,
      )
      console.log(
        `💰 Price range for ${zone}: €${Math.min(...priceData.map((d) => d.spotPrice)).toFixed(2)} - €${Math.max(...priceData.map((d) => d.spotPrice)).toFixed(2)}/MWh`,
      )

      return priceData
    } else {
      console.warn(`⚠️  No price data parsed from XML for ${zone}`)
      throw new Error('No data in response')
    }
  } catch (error: any) {
    // Track failure
    const currentFailures = failedAttempts.get(cacheKey) || {
      count: 0,
      lastAttempt: 0,
    }
    failedAttempts.set(cacheKey, {
      count: currentFailures.count + 1,
      lastAttempt: Date.now(),
    })

    console.error(`❌ ENTSO-E fetch failed for ${zone}:`, error.message)
    console.error(`   Failure count: ${currentFailures.count + 1}`)

    const fallbackData = generateMarketData(zone, 24)

    // Cache fallback to prevent repeated failures
    entsoeCache.set(cacheKey, { data: fallbackData, timestamp: Date.now() })

    return fallbackData
  }
}

/**
 * Format date for ENTSO-E API (YYYYMMDDHHmm in UTC)
 */
function formatEntsoeDate(date: Date): string {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  const hours = String(date.getUTCHours()).padStart(2, '0')
  const minutes = String(date.getUTCMinutes()).padStart(2, '0')
  return `${year}${month}${day}${hours}${minutes}`
}

/**
 * Parse ENTSO-E XML response to extract price data
 * Handles ENTSO-E's complex XML structure with namespaces
 */
function parseEntsoeXML(xmlText: string, zone: MarketZone): MarketData[] {
  try {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml')

    // Check for XML parsing errors
    const parseError = xmlDoc.querySelector('parsererror')
    if (parseError) {
      console.error(`${zone}: XML parse error`)
      return []
    }

    // Check for ENTSO-E error messages
    const errorText = xmlDoc.querySelector('Reason text')?.textContent
    if (errorText) {
      console.error(`${zone}: API error - ${errorText}`)
      return []
    }

    // Try to find TimeSeries elements (case-insensitive)
    let timeSeries = xmlDoc.getElementsByTagName('TimeSeries')
    if (timeSeries.length === 0) {
      // Try lowercase
      timeSeries = xmlDoc.getElementsByTagName('timeSeries')
    }

    if (timeSeries.length === 0) {
      console.error(`${zone}: No TimeSeries found`)
      return []
    }

    console.log(`${zone}: Found ${timeSeries.length} TimeSeries elements`)

    // Get the first TimeSeries
    const series = timeSeries[0]

    // Find Period element
    let periods = series.getElementsByTagName('Period')
    if (periods.length === 0) {
      periods = series.getElementsByTagName('period')
    }

    if (periods.length === 0) {
      console.error(`${zone}: No Period found`)
      return []
    }

    console.log(`${zone}: Found ${periods.length} Period elements`)

    const period = periods[0]

    // Get start time from timeInterval
    let timeInterval = period.getElementsByTagName('timeInterval')[0]
    if (!timeInterval) {
      timeInterval = period.getElementsByTagName('TimeInterval')[0]
    }

    let startElement = timeInterval?.getElementsByTagName('start')[0]
    if (!startElement) {
      startElement = timeInterval?.getElementsByTagName('Start')[0]
    }

    const periodStart = startElement?.textContent

    if (!periodStart) {
      console.error(`${zone}: No start time found`)
      return []
    }

    const startTime = new Date(periodStart)
    console.log(`${zone}: Period starts at ${startTime.toISOString()}`)

    // Get all Point elements
    let points = period.getElementsByTagName('Point')
    if (points.length === 0) {
      points = period.getElementsByTagName('point')
    }

    if (points.length === 0) {
      console.error(`${zone}: No Point elements found`)
      return []
    }

    console.log(`${zone}: Found ${points.length} Point elements`)

    const marketData: MarketData[] = []

    for (let i = 0; i < points.length; i++) {
      const point = points[i]

      let positionElement = point.getElementsByTagName('position')[0]
      if (!positionElement) {
        positionElement = point.getElementsByTagName('Position')[0]
      }

      let priceElement = point.getElementsByTagName('price.amount')[0]
      if (!priceElement) {
        priceElement = point.getElementsByTagName('Price.amount')[0]
      }
      if (!priceElement) {
        priceElement = point.getElementsByTagName('price_amount')[0]
      }

      const position = positionElement?.textContent
      const priceAmount = priceElement?.textContent

      if (position && priceAmount) {
        const hourOffset = parseInt(position) - 1
        const timestamp = new Date(
          startTime.getTime() + hourOffset * 60 * 60 * 1000,
        )
        const price = parseFloat(priceAmount)

        // Generate realistic generation data based on the price
        const baseGen = generateMarketData(zone, 1)[0].generation
        const priceMultiplier = Math.max(0.5, Math.min(1.5, price / 85))

        const totalLoad =
          baseGen.wind +
          baseGen.solar +
          baseGen.nuclear +
          baseGen.gas +
          baseGen.coal +
          baseGen.hydro +
          baseGen.biomass +
          baseGen.other

        marketData.push({
          timestamp,
          zone,
          spotPrice: price,
          dayAheadPrice: price,
          generation: {
            wind: baseGen.wind * (2 - priceMultiplier) * 0.8,
            solar: baseGen.solar * (2 - priceMultiplier) * 0.8,
            nuclear: baseGen.nuclear,
            gas: baseGen.gas * priceMultiplier,
            coal: baseGen.coal * priceMultiplier,
            hydro: baseGen.hydro,
            biomass: baseGen.biomass,
            other: baseGen.other,
          },
          load: totalLoad,
          capacity: {
            installed: totalLoad * 1.5,
            available: totalLoad * 1.3,
          },
          crossBorderFlow: (Math.random() - 0.5) * 5000,
        })
      } else {
        console.warn(`${zone}: Point ${i} missing position or price`)
      }
    }

    console.log(`${zone}: Successfully parsed ${marketData.length} data points`)
    return marketData
  } catch (error: any) {
    console.error(`${zone}: Parse error - ${error.message}`)
    return []
  }
}

/**
 * Generate model accuracy metrics
 */
export function generateModelAccuracy(): ModelAccuracy[] {
  return [
    {
      model: 'EPT-2',
      mae: 0.45,
      rmse: 0.62,
      bias: -0.05,
      score: 94,
      lastUpdated: new Date(),
    },
    {
      model: 'EC-IFS',
      mae: 0.58,
      rmse: 0.78,
      bias: 0.12,
      score: 89,
      lastUpdated: new Date(),
    },
    {
      model: 'GFS',
      mae: 0.72,
      rmse: 0.95,
      bias: -0.18,
      score: 82,
      lastUpdated: new Date(),
    },
    {
      model: 'EPT-2 Early',
      mae: 0.51,
      rmse: 0.69,
      bias: 0.08,
      score: 91,
      lastUpdated: new Date(),
    },
  ]
}

/**
 * Generate price predictions
 */
export function generatePricePredictions(
  zone: MarketZone,
  hours: number = 48,
): PricePrediction[] {
  const predictions: PricePrediction[] = []
  const now = new Date()
  const basePrice = generateMarketData(zone, 1)[0].spotPrice

  for (let i = 0; i < hours; i++) {
    const timestamp = new Date(now.getTime() + i * 60 * 60 * 1000)
    const hour = timestamp.getHours()
    const peakMultiplier = hour >= 8 && hour <= 20 ? 1.15 : 0.85

    const predictedPrice =
      basePrice * peakMultiplier * (0.95 + Math.random() * 0.1)

    predictions.push({
      timestamp,
      zone,
      predictedPrice,
      confidence: {
        lower: predictedPrice * 0.9,
        upper: predictedPrice * 1.1,
      },
      probability: 0.7 + Math.random() * 0.25,
      model: 'EPT-2 Oracle',
    })
  }

  return predictions
}
